import { Skeleton } from '@app/components/various/Skeleton';
import { DEFAULT_TEXTAREA_MAX_LENGTH } from '@app/constants/limits';
import { isDefined } from '@app/utils/is';
import { Textarea as TextareaNs } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Textarea.module.scss';

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaNs.Props>((props, ref) => {
  const {
    dataTestId,
    describedById,
    errorMessageId,
    isDisabled,
    isInvalid = false,
    isReadonly = false,
    isRequired = false,
    isLoading = false,
    className,
    onChange,
    maxLength,
    ...rest
  } = props;
  const [text, setText] = React.useState('');
  const { t } = useTranslation(['common']);
  const innerOnChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = event.target;

      if (!isDefined(maxLength) || value.length <= maxLength) {
        setText(value);
        onChange?.(event);
      }
    },
    [maxLength, text],
  );

  const classNames = cx(styles.textarea, className);

  return (
    <span className="relative block">
      {isLoading && <Skeleton type="rectangle" className="absolute inset-0" />}
      <textarea
        ref={ref}
        aria-describedby={describedById}
        aria-errormessage={errorMessageId}
        aria-invalid={isInvalid}
        className={classNames}
        data-testid={dataTestId}
        disabled={isDisabled}
        maxLength={maxLength ?? DEFAULT_TEXTAREA_MAX_LENGTH}
        readOnly={isReadonly}
        required={isRequired}
        rows={6}
        onChange={innerOnChange}
        {...rest}
      />
      {isDefined(maxLength) && (
        <span className="flex justify-end">
          <span className="text-neutral-60 body-5">{t('common:textarea_characters', { actualLength: text.length, maxLength })}</span>
        </span>
      )}
    </span>
  );
});
